import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewContainerRef
} from '@angular/core';
import { DropdownMenuComponent } from './dropdown-menu.component';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subject } from 'rxjs';

type voidFunc = () => void;

@Directive({
  selector: '[mtgDropdownTriggerFor]'
})
export class DropdownTriggerDirective implements OnInit, OnDestroy {
  private overlayRef: OverlayRef;

  // tslint:disable-next-line:no-input-rename
  @Input('mtgDropdownTriggerFor')
  dropdownMenu: DropdownMenuComponent;
  // tslint:disable-next-line:no-input-rename
  @Input('mtgDropdownPosition')
  position: 'left' | 'right' = 'right';
  // tslint:disable-next-line:no-input-rename
  @Input('mtgDropdownActiveClass')
  activeClass: string;
  @Input('mtgDropdownDisabled')
  disabled: boolean;
  subscriptions: voidFunc[] = [];
  isVisible: boolean;
  isVisible$ = new Subject<boolean>();

  constructor(
    private viewContainerRef: ViewContainerRef,
    private overlay: Overlay,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.renderer.listen('window', 'click', $event => {
        if (!this.isVisible) {
          return;
        }
        const target = $event.composedPath() && $event.composedPath()[0] || $event.target;
        if (!this.overlayRef.hostElement.contains(target) &&
          !this.elementRef.nativeElement.contains(target)
        ) {
          this.hideMenu();
          this.cdr.markForCheck();
        }
      }),
      this.renderer.listen('window', 'resize', () => {
        this.syncWidth();
      })
    );
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
    for (const unsubscribe of this.subscriptions) {
      unsubscribe();
    }
    this.isVisible$.complete();
  }

  @HostListener('click', ['$event'])
  onClick($event) {
    if (!this.isVisible) {
      this.showMenu();
    } else {
      this.hideMenu();
    }
  }

  showMenu() {
    if (this.overlayRef || this.disabled) {
      return;
    }

    this.isVisible = true;
    this.isVisible$.next(true);
    this.overlayRef = this.overlay.create({
      positionStrategy: this.getPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    this.dropdownMenu.trigger = this;
    const templatePortal = new TemplatePortal(this.dropdownMenu.templateRef, this.viewContainerRef);
    this.overlayRef.attach(templatePortal);
    if (this.activeClass) {
      this.elementRef.nativeElement.classList.add(this.activeClass);
    }
    this.syncWidth();
  }

  hideMenu() {
    if (!this.overlayRef) {
      return;
    }
    this.isVisible = false;
    this.isVisible$.next(false);
    this.overlayRef.dispose();
    delete this.overlayRef;
    if (this.activeClass) {
      this.elementRef.nativeElement.classList.remove(this.activeClass);
    }
  }

  getPositionStrategy() {
    if (this.position === 'left') {
      return this.overlay.position()
        .flexibleConnectedTo(this.elementRef.nativeElement)
        .withFlexibleDimensions(true)
        .withPush(false)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
          }
        ]);
    } else {
      return this.overlay.position()
        .flexibleConnectedTo(this.elementRef.nativeElement)
        .withFlexibleDimensions(true)
        .withPush(false)
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom'
          },
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
          }
        ]);
    }
  }

  private syncWidth() {
    if (!this.overlayRef) {
      return;
    }
    const boundBox = this.elementRef.nativeElement.getBoundingClientRect();
    this.overlayRef.updateSize({
      minWidth: boundBox.width
    });
  }
}
